// taskApi.js - API utilities for task operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for task API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_task',
        `Task API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[taskApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_task',
      `Task API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[taskApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Task Management.
 */
export const taskApi = {
  /**
   * Fetches tasks for a specific date.
   * GET /tasks/v2?date={formattedDate}
   * @param {Date|string} [date] - The date to fetch tasks for. If not provided, uses current date.
   * @returns {Promise<Object>} Object containing task data
   * @throws {Error} - Throws an error if the API request fails
   */
  getTasks: async (date = null) => {
    try {
      // Format the date - use provided date or default to current date
      let formattedDate;
      
      if (date) {
        if (date instanceof Date) {
          formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        } else if (typeof date === 'string') {
          // Validate string date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(date)) {
            throw new Error('Invalid date format. Expected YYYY-MM-DD');
          }
          formattedDate = date;
        } else {
          throw new Error('Invalid date parameter. Expected Date object or YYYY-MM-DD string');
        }
      } else {
        // Default to current date
        const now = new Date();
        formattedDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
      }

      console.log('Task API: Fetching tasks for date:', formattedDate);

      const response = await makeApiRequest(`/tasks/v2?date=${formattedDate}`);
      const responseData = await response.json();

      console.log('Task API: Response status:', response.status);
      console.log('Task API: Response data:', responseData);

      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_task',
        `Successfully fetched tasks for date ${formattedDate}`,
        {
          endpoint: '/tasks/v2',
          method: 'GET',
          date: formattedDate,
        }
      ).catch(logError => {
        console.warn('[taskApi] Failed to log success event:', logError);
      });

      return responseData;
    } catch (error) {
      console.error('Task API: Error in getTasks:', error);
      
      // Handle specific error cases based on status codes
      if (error.status === 400) {
        throw new Error('Invalid date parameter or request format');
      } else if (error.status === 500) {
        throw new Error('Internal server error while fetching tasks');
      } else {
        throw new Error(`Error fetching tasks: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all ended tasks and issues.
   * GET /Issue-or-task/getall-ended
   * @returns {Promise<Object>} Object containing ended task and issue data
   * @throws {Error} - Throws an error if the API request fails
   */
  getAllEndedTasksAndIssues: async () => {
    try {
      console.log('Task API: Fetching all ended tasks and issues');

      const response = await makeApiRequest('/Issue-or-task/getall-ended');
      const responseData = await response.json();

      console.log('Task API: Response status:', response.status);
      console.log('Task API: Response data:', responseData);

      if (response.status === 200) {
        // Log API event for successful fetch
        loggingService.logInfo(
          'api_success_task',
          `Successfully fetched all ended tasks and issues`,
          {
            endpoint: '/Issue-or-task/getall-ended',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[taskApi] Failed to log success event:', logError);
        });
        
        return responseData;
      } else if (response.status === 500) {
        throw new Error(`Server error: ${responseData.error || 'Unknown server error'}`);
      } else {
        throw new Error(`Failed to fetch ended tasks and issues: ${response.status} - ${JSON.stringify(responseData)}`);
      }
    } catch (error) {
      console.error('Task API: Error in getAllEndedTasksAndIssues:', error);
      
      // Handle specific error cases based on status codes
      if (error.status === 500) {
        throw new Error(`Server error: ${error.responseData?.error || 'Unknown server error'}`);
      } else {
        throw new Error(`Error fetching ended tasks and issues: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all not ended tasks and issues.
   * GET /Issue-or-task/getall-not-ended
   * @returns {Promise<Object>} Object containing not ended task and issue data
   * @throws {Error} - Throws an error if the API request fails
   */
  getAllNotEndedTasksAndIssues: async () => {
    try {
      console.log('Task API: Fetching all not ended tasks and issues');

      const response = await makeApiRequest('/Issue-or-task/getall-not-ended');
      const responseData = await response.json();

      console.log('Task API: Response status:', response.status);
      console.log('Task API: Response data:', responseData);

      if (response.status === 200) {
        // Log API event for successful fetch
        loggingService.logInfo(
          'api_success_task',
          `Successfully fetched all not ended tasks and issues`,
          {
            endpoint: '/Issue-or-task/getall-not-ended',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[taskApi] Failed to log success event:', logError);
        });
        
        return responseData;
      } else if (response.status === 500) {
        throw new Error(`Server error: ${responseData.error || 'Unknown server error'}`);
      } else {
        throw new Error(`Failed to fetch not ended tasks and issues: ${response.status} - ${JSON.stringify(responseData)}`);
      }
    } catch (error) {
      console.error('Task API: Error in getAllNotEndedTasksAndIssues:', error);
      
      // Handle specific error cases based on status codes
      if (error.status === 500) {
        throw new Error(`Server error: ${error.responseData?.error || 'Unknown server error'}`);
      } else {
        throw new Error(`Error fetching not ended tasks and issues: ${error.message}`);
      }
    }
  },

  /**
   * Helper function to format a Date object to YYYY-MM-DD string format.
   * @param {Date} date - The date to format
   * @returns {string} Formatted date string in YYYY-MM-DD format
   */
  formatDateForApi: (date) => {
    if (!(date instanceof Date)) {
      throw new Error('Invalid date parameter. Expected Date object');
    }
    
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  },

  /**
   * Helper function to get current date in YYYY-MM-DD format.
   * @returns {string} Current date in YYYY-MM-DD format
   */
  getCurrentDateString: () => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
  },

  /**
   * Helper function to filter tasks from the combined tasks and issues data.
   * @param {Array} data - Array of tasks and issues
   * @returns {Array} Array containing only tasks (taskType: "Task")
   */
  filterTasks: (data) => {
    if (!Array.isArray(data)) {
      console.warn('Task API: filterTasks received non-array data:', data);
      return [];
    }
    return data.filter(item => item.taskType === "Task");
  },

  /**
   * Helper function to filter issues from the combined tasks and issues data.
   * @param {Array} data - Array of tasks and issues
   * @returns {Array} Array containing only issues (taskType: "Issue")
   */
  filterIssues: (data) => {
    if (!Array.isArray(data)) {
      console.warn('Task API: filterIssues received non-array data:', data);
      return [];
    }
    return data.filter(item => item.taskType === "Issue");
  },

  /**
   * Helper function to get issues count by status.
   * @param {Array} issues - Array of issues
   * @param {string} status - Status to filter by (e.g., "Open", "Closed", "In Progress")
   * @returns {number} Count of issues with the specified status
   */
  getIssuesCountByStatus: (issues, status) => {
    if (!Array.isArray(issues)) {
      console.warn('Task API: getIssuesCountByStatus received non-array data:', issues);
      return 0;
    }
    return issues.filter(issue => issue.status === status).length;
  },

  /**
   * Helper function to get total issues count.
   * @param {Array} issues - Array of issues
   * @returns {number} Total count of issues
   */
  getTotalIssuesCount: (issues) => {
    if (!Array.isArray(issues)) {
      console.warn('Task API: getTotalIssuesCount received non-array data:', issues);
      return 0;
    }
    return issues.length;
  }
};
